import { Check, Copy, FileText } from 'lucide-react'
import { useState } from 'react'
import type { WorkResultResponse } from '../../types/work'
import WorkDownloadButton from './WorkDownloadButton'

interface WorkResultViewerProps {
  result: WorkResultResponse
}

export default function WorkResultViewer({ result }: WorkResultViewerProps) {
  const [copied, setCopied] = useState(false)

  async function handleCopy(text: string) {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => { setCopied(false) }, 2000)
  }

  // Text result
  if (result.result_type === 'text') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>Result</span>
          <button
            onClick={() => { void handleCopy(result.result_text || '') }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '14px',
            borderRadius: '10px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            fontSize: '13px',
            lineHeight: 1.7,
            color: 'var(--text-primary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {result.result_text || '(empty)'}
        </div>
        {result.token_count && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>
            {result.token_count} tokens · {result.processing_time_ms}ms
          </span>
        )}
      </div>
    )
  }

  // File result
  if (result.result_type === 'file') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          borderRadius: '10px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FileText size={20} style={{ color: 'var(--primary, #6366f1)' }} />
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
              Output file ready
            </div>
            {result.result_preview && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted, #6b7280)', marginTop: '2px' }}>
                {result.result_preview}
              </div>
            )}
          </div>
        </div>
        {result.download_url && result.download_expires_at && (
          <WorkDownloadButton
            taskId={result.task_id}
            expiresAt={result.download_expires_at}
            downloadUrl={result.download_url}
          />
        )}
      </div>
    )
  }

  // JSON result
  if (result.result_type === 'json') {
    const jsonStr = JSON.stringify(result.result_json, null, 2)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>JSON Result</span>
          <button
            onClick={() => { void handleCopy(jsonStr) }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: 'var(--card-bg)',
              border: '1px solid var(--border-color)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <pre
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '14px',
            borderRadius: '10px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            fontSize: '12px',
            lineHeight: 1.6,
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          {jsonStr}
        </pre>
      </div>
    )
  }

  // Embedding result
  if (result.result_type === 'embedding') {
    const dims = Array.isArray(result.result_json) ? (result.result_json as unknown[]).length : '?'
    return (
      <div
        style={{
          padding: '14px 16px',
          borderRadius: '10px',
          background: 'rgba(99, 102, 241, 0.08)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          fontSize: '13px',
          color: 'var(--text-primary)',
        }}
      >
        Embedding vector generated ({dims} dimensions)
        {result.processing_time_ms && (
          <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--text-muted, #6b7280)' }}>
            · {result.processing_time_ms}ms
          </span>
        )}
      </div>
    )
  }

  return null
}
